/**
 * Pure color math utilities for deriving muted/border/foreground
 * CSS variable overrides from tenant app background & text colors.
 *
 * No 'server-only' or 'use client' — importable from both contexts. The core
 * WCAG math lives in @kit/shared/color so customer emails share one algorithm.
 */
import {
  contrastRatio,
  deriveContrastingForeground,
  hexToRgb,
  isDarkColor,
  luminance,
  mixHex,
  rgbToHex,
} from '@kit/shared/color';

export { contrastRatio, deriveContrastingForeground, isDarkColor };

/**
 * Derive --muted from background: shift slightly toward mid-gray.
 * Dark bg → slightly lighter; light bg → slightly darker.
 */
export function deriveMutedColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex);
  const shift = isDarkColor(bgHex) ? 20 : -15;

  return rgbToHex(r + shift, g + shift, b + shift);
}

/**
 * Derive --muted-foreground: blend text color 60% toward background
 * to produce a "dimmed" text color.
 */
export function deriveMutedForeground(textHex: string, bgHex?: string): string {
  const text = hexToRgb(textHex);
  const bg = bgHex ? hexToRgb(bgHex) : { r: 255, g: 255, b: 255 };
  const alpha = 0.6;

  return rgbToHex(
    text.r * alpha + bg.r * (1 - alpha),
    text.g * alpha + bg.g * (1 - alpha),
    text.b * alpha + bg.b * (1 - alpha),
  );
}

/**
 * Derive --border from background: subtle contrast line.
 */
export function deriveBorderColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex);
  const shift = isDarkColor(bgHex) ? 30 : -20;

  return rgbToHex(r + shift, g + shift, b + shift);
}

/** Subtle brand tint with readable selected-state ink on light or dark surfaces. */
export function deriveSelectionColors(
  background: string,
  accent: string,
  ink: string,
) {
  const fill = mixHex(background, accent, 0.14);
  const fillLuminance = luminance(fill);
  const contrast = (color: string) => {
    const value = luminance(color);
    return (
      (Math.max(value, fillLuminance) + 0.05) /
      (Math.min(value, fillLuminance) + 0.05)
    );
  };
  const target =
    contrast('#000000') > contrast('#ffffff') ? '#000000' : '#ffffff';
  let foreground = ink;
  for (let step = 1; contrast(foreground) < 4.5 && step <= 20; step++) {
    foreground = mixHex(ink, target, step / 20);
  }
  return { background: fill, foreground, border: foreground };
}
